import { ChevronRight } from "lucide-react";
import { cn } from "@/frontend/lib/utils";
import {
  computeExportProgress,
  formatTimeAgo,
  humanizeExportError,
} from "@/frontend/utils/dashboard/settings/export";
import { getDestinationLogo, getDestTypeLabel } from "@/frontend/utils/dashboard/settings/activity";

/**
 * Renders a single export destination as a clickable row. The whole card
 * navigates to the detail view where edit / disable / delete live. Shows
 * inline progress when an export is running, and a failure indicator when
 * the last export failed.
 *
 * @param destination - The export destination object to display
 * @param onClick - Callback invoked when the card is clicked (navigates to detail view)
 */
export function DestinationCard({
  destination,
  onClick,
}: {
  destination: any;
  onClick: () => void;
}) {
  const isActive = destination.enabled;
  const typeLabel = getDestTypeLabel(destination);

  const activeStatus = destination.activeRunStatus as string | null;
  const isRunning = activeStatus === "running";
  const isQueued = activeStatus === "queued";
  const hasActiveRun = isRunning || isQueued;

  // Progress is resource-wise for FHIR destinations, account-wise for S3.
  const { total, pct } = computeExportProgress(destination);

  const lastFailed = destination.lastFailedAt as string | null;
  const lastSucceeded = destination.lastRunCompletedAt as string | null;
  const showFailure =
    lastFailed &&
    (!lastSucceeded || new Date(lastFailed) > new Date(lastSucceeded));

  return (
    <div
      onClick={onClick}
      className={cn(
        "group flex flex-col gap-0 rounded-lg border transition-colors cursor-pointer",
        hasActiveRun ? "border-blue-500/20" : "hover:bg-muted/30"
      )}
    >
      {/* Main row */}
      <div className="flex items-center gap-4 px-4 py-4">
        <img
          src={getDestinationLogo(destination)}
          alt={typeLabel}
          className="w-6 h-6 object-contain shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{destination.name}</span>
            <span
              className={cn(
                "inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium capitalize",
                isActive
                  ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                  : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
              )}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
            {hasActiveRun && (
              <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                {isQueued ? "Queued" : "Exporting"}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {typeLabel}
            {!hasActiveRun &&
              destination.lastRunCompletedAt &&
              ` · Last export: ${formatTimeAgo(destination.lastRunCompletedAt)}`}
            {!hasActiveRun &&
              destination.lastRunPatients != null &&
              ` · ${destination.lastRunPatients} patients`}
            {!hasActiveRun &&
              destination.lastRunResources != null &&
              ` · ${destination.lastRunResources} resources`}
          </div>
        </div>

        {/* Affordance: the whole card is clickable; chevron cues the nav */}
        <ChevronRight className="size-4 text-muted-foreground shrink-0 transition-transform group-hover:translate-x-0.5" />
      </div>

      {/* Running progress bar — only shown once the denominator is known */}
      {isRunning && total > 0 && (
        <div className="flex items-center gap-3 px-4 pb-3">
          <div className="h-1.5 bg-blue-200 dark:bg-blue-800/30 rounded-full flex-1 overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium shrink-0">
            {pct}%
          </span>
        </div>
      )}

      {/* Failure indicator */}
      {showFailure && !hasActiveRun && (
        <div className="mx-4 mb-3 px-3 py-2 rounded-md bg-red-50 dark:bg-red-500/5 border border-red-200 dark:border-red-500/20">
          <p className="text-xs font-medium text-red-600 dark:text-red-400">
            {humanizeExportError(destination.lastFailedError)} · {formatTimeAgo(destination.lastFailedAt)}
          </p>
        </div>
      )}
    </div>
  );
}
